import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { useAppStore } from '@/store/AppStore';
import { SERVICES, REGIONS, SOURCES, RESPONSIBLES, PRIORITIES } from '@/mocks/constants';
import type { QuoteRequest, Priority } from '@/types';

const inputClass =
  'h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';
const labelClass = 'mb-1.5 block text-xs font-semibold text-foreground-600';

export default function RequestForm({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { customers, addRequest } = useAppStore();
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [service, setService] = useState(SERVICES[0]);
  const [region, setRegion] = useState(REGIONS[0]);
  const [source, setSource] = useState('WhatsApp');
  const [responsible, setResponsible] = useState(RESPONSIBLES[0]);
  const [priority, setPriority] = useState<Priority>('Normal');
  const [propertyType, setPropertyType] = useState('');
  const [area, setArea] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [addressNote, setAddressNote] = useState('');
  const [error, setError] = useState('');

  function reset() {
    setCustomerName('');
    setPhone('');
    setService(SERVICES[0]);
    setRegion(REGIONS[0]);
    setSource('WhatsApp');
    setResponsible(RESPONSIBLES[0]);
    setPriority('Normal');
    setPropertyType('');
    setArea('');
    setCustomerNote('');
    setAddressNote('');
    setError('');
  }

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleSelectCustomer(name: string) {
    setCustomerName(name);
    const c = customers.find((x) => x.name === name);
    if (c) setPhone(c.phone);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customerName.trim() || !phone.trim()) {
      setError('Müşteri adı ve telefon zorunludur.');
      return;
    }
    const request: QuoteRequest = {
      id: `TR-2026-${String(Date.now()).slice(-4)}`,
      customerName: customerName.trim(),
      phone: phone.trim(),
      service,
      region,
      requestDate: '25 Ağustos 2026',
      videoStatus: 'Video Yok',
      status: 'Yeni Talep',
      source,
      responsible,
      lastContact: 'Şimdi',
      priority,
      propertyType: propertyType || 'Belirtilmedi',
      area: area || '-',
      customerNote: customerNote || '',
      addressNote: addressNote || '',
      activities: [{ time: 'Şimdi', text: 'Talep geldi (manuel)' }],
    };
    addRequest(request);
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Yeni Talep"
      maxWidth="max-w-xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-background-200 px-4 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            form="request-form"
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Talebi Kaydet
          </button>
        </>
      }
    >
      <form id="request-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelClass}>Müşteri</label>
            <select
              className={inputClass}
              value={customerName}
              onChange={(e) => handleSelectCustomer(e.target.value)}
            >
              <option value="">Müşteri seç veya manuel yaz</option>
              {customers.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Müşteri Adı</label>
            <input
              className={inputClass}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Ad Soyad"
            />
          </div>
          <div>
            <label className={labelClass}>Telefon</label>
            <input
              className={inputClass}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="05xx xxx xx xx"
            />
          </div>
          <div>
            <label className={labelClass}>Hizmet</label>
            <select className={inputClass} value={service} onChange={(e) => setService(e.target.value)}>
              {SERVICES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Bölge</label>
            <select className={inputClass} value={region} onChange={(e) => setRegion(e.target.value)}>
              {REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Kaynak</label>
            <select className={inputClass} value={source} onChange={(e) => setSource(e.target.value)}>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Sorumlu</label>
            <select
              className={inputClass}
              value={responsible}
              onChange={(e) => setResponsible(e.target.value)}
            >
              {RESPONSIBLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Öncelik</label>
            <select
              className={inputClass}
              value={priority}
              onChange={(e) => setPriority(e.target.value as Priority)}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Mülk Tipi</label>
            <input
              className={inputClass}
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value)}
              placeholder="Villa, Daire, Ofis..."
            />
          </div>
          <div>
            <label className={labelClass}>Yaklaşık Alan</label>
            <input
              className={inputClass}
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="150 m²"
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>Müşteri Notu</label>
          <textarea
            className="h-20 w-full resize-none rounded-md border border-background-200 bg-white px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            value={customerNote}
            onChange={(e) => setCustomerNote(e.target.value)}
            placeholder="Müşterinin talebiyle ilgili not"
          />
        </div>
        <div>
          <label className={labelClass}>Adres Notu</label>
          <input
            className={inputClass}
            value={addressNote}
            onChange={(e) => setAddressNote(e.target.value)}
            placeholder="Yol tarifi notu (opsiyonel)"
          />
        </div>
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</p>
        )}
      </form>
    </Modal>
  );
}